import ExamSectionTemplate from '@/pages/ExamSectionTemplate';

const faqs = [
  {
    question: "What is CBE-LMM certification?",
    answer: "CBE-LMM (Competency Board Examination in Longevity & Metabolic Medicine) validates clinical competency in longevity science, metabolic health optimization, gut microbiome therapy, immune modulation, and evidence-based nutraceutical therapeutics. It is designed for healthcare professionals seeking formal competency recognition in preventive and metabolic healthcare."
  },
  {
    question: "Who should pursue CBE-LMM certification?",
    answer: "CBE-LMM is designed for licensed healthcare professionals including MBBS doctors, BAMS, BUMS, BHMS, BNYS practitioners, and other qualified healthcare providers who practice or wish to specialize in longevity medicine, metabolic health optimization, and nutraceutical therapeutics."
  },
  {
    question: "What clinical domains does CBE-LMM cover?",
    answer: "CBE-LMM assesses competency across six core domains: Longevity Science & Cellular Biology (15%), Metabolic Health & Optimization (20%), Nutraceutical Therapeutics (20%), Gut Microbiome & Digestive Health (15%), Immune Modulation & Anti-Inflammatory Strategies (15%), and Clinical Application & Protocol Development (15%)."
  },
  {
    question: "How is the CBE-LMM examination structured?",
    answer: "CBE-LMM consists of two components: a Written Assessment (100 MCQs, 3 hours) covering theoretical knowledge across all domains with case-based scenarios, and a Practical Assessment (2 cases, 2 hours) involving clinical case presentations and personalized protocol development demonstrations."
  },
  {
    question: "What is the difference between CBE-LMM and CBELM?",
    answer: "CBELM focuses on anti-aging and hormone optimization therapies, while CBE-LMM emphasizes metabolic health, gut microbiome therapy, nutraceutical therapeutics, and preventive medicine protocols. CBE-LMM is also accessible to a broader range of healthcare professionals including AYUSH practitioners."
  },
  {
    question: "What are the passing criteria for CBE-LMM?",
    answer: "The minimum passing score is 60%. Candidates achieving 75% or above receive Merit Distinction recognition, and those achieving 90% or above receive the Excellence Award. Both components (Written and Practical) must be passed independently."
  },
  {
    question: "Can I practice independently after CBE-LMM certification?",
    answer: "CBE-LMM certification validates assessed competencies in longevity and metabolic medicine but does not grant independent medical licensure. Practitioners must hold valid professional registration in their jurisdiction and ensure their practice aligns with local regulatory frameworks."
  }
];

export default function CBELMMPage() {
  return (
    <ExamSectionTemplate
      sectionTitle="Longevity & Metabolic Medicine"
      examCode="CBE-LMM"
      ipcfBadge="Level 6 Practitioner Certification"
      examFullName="Competency Board Examination in Longevity & Metabolic Medicine"
      testIdPrefix="cbelmm"
      fee="₹25,000"
      feeLabel="Examination Fee (India)"
      faqs={faqs}
      overview="CBE-LMM assesses clinical competency in longevity science, metabolic health optimization, gut microbiome therapy, immune modulation, and evidence-based nutraceutical therapeutics. This examination validates practitioners' ability to design and implement preventive healthcare protocols, metabolic interventions, and personalized nutraceutical treatment plans. The certification is designed for healthcare professionals across multiple disciplines seeking formal recognition in this rapidly growing specialty."
      eligibility={[
        'Valid medical/healthcare registration in home jurisdiction (MBBS, BAMS, BUMS, BHMS, BNYS, or equivalent)',
        'Minimum 2 years post-licensure clinical experience',
        'Evidence of training or practice in metabolic medicine, nutrition, or preventive healthcare',
        'Professional references from two registered healthcare practitioners',
        'Compliance with IMCAC ethics and conduct standards',
        'Current professional indemnity insurance',
      ]}
      stages={[
        { title: 'Written Assessment', desc: 'Multiple Choice Questions (100 MCQs, 3 hours) covering theoretical knowledge across all six examination domains with case-based clinical scenarios.' },
        { title: 'Practical Assessment', desc: 'Clinical case presentations (2 cases, 2 hours) demonstrating assessment skills and personalized nutraceutical protocol development.' },
        { title: 'Protocol Evaluation', desc: 'Assessment of submitted treatment protocols evaluating evidence-based approach, safety considerations, and patient-centered care.' },
        { title: 'Viva Examination', desc: 'Oral examination assessing clinical reasoning, evidence appraisal, ethical considerations, and integration of nutraceutical therapeutics.' },
      ]}
      coreDomains={[
        'Longevity Science & Cellular Biology',
        'Metabolic Health & Insulin Resistance',
        'Nutraceutical Therapeutics',
        'Gut Microbiome & Digestive Health',
        'Immune Modulation Strategies',
        'Clinical Protocol Development',
        'Nutritional Assessment',
        'Lifestyle Medicine Integration',
        'Patient Safety & Ethics',
      ]}
      certificationValidity={[
        'Initial certification valid for 5 years from date of issue',
        'Certificate verifiable through IMCAC public verification portal',
        'Certification title: Board Certified in Longevity & Metabolic Medicine (IMCAC – CBE-LMM)',
        'Digital certificate with blockchain verification available',
        'Certification may be revoked for non-compliance with CPD or ethics requirements',
      ]}
      recertificationPolicy={[
        'Recertification required every 5 years',
        'Minimum 25 CPD hours per year in relevant longevity and metabolic medicine activities',
        'Evidence of continued clinical practice in the specialty',
        'Submission of case portfolio demonstrating ongoing competency',
        'Compliance audit by IMCAC CPD Committee',
        'Non-compliance may result in suspension of certification status',
      ]}
      disclaimer="CBE-LMM certification validates assessed competencies within longevity, metabolic, and nutraceutical medicine and does not grant medical licensure or independent practice rights. Candidates must hold valid professional registration in their jurisdiction. The scope of practice after certification remains governed by local regulatory frameworks. Nutraceutical recommendations must comply with applicable food and drug regulations in the practitioner's jurisdiction."
      additionalDisclaimer="Practitioners should note that nutraceutical and metabolic interventions are subject to varying regulatory frameworks across different jurisdictions. It is the practitioner's responsibility to ensure compliance with local regulations regarding therapeutic claims, product recommendations, and scope of practice limitations."
      applyLink="/apply"
    />
  );
}
